import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { useDealer } from '../../context/DealerContext';
import { dealerApi, Balance, Credit, CreditRequest } from '../../services/dealerApi';
import Money from '../../components/dealer/Money';
import StatusChip from '../../components/dealer/StatusChip';
import CreditSummaryTable from '../../components/dealer/CreditSummaryTable';
import ProformaAcceptDialog from '../../components/dealer/ProformaAcceptDialog';
import CommitSpendNote from '../../components/dealer/CommitSpendNote';
import { dealerErrorMessage, formatDate } from '../../utils/dealer';

type Tab = 'requests' | 'balance' | 'assigned';
const TABS: Tab[] = ['requests', 'balance', 'assigned'];
const TABS_WITHOUT_WALLET: Tab[] = ['requests', 'assigned'];

/**
 * Spec 085 (§4) — Credit: tabs Requests · Balance · Assigned, with the request / pack / assign entry points. With the
 * wallet switch off the Balance tab and the pack / assign entry points are gone (and `/balance` is never called).
 */
export default function DealerCreditPage() {
  const { t } = useLanguage();
  const { walletEnabled } = useDealer();
  const [params, setParams] = useSearchParams();
  const tabs = walletEnabled ? TABS : TABS_WITHOUT_WALLET;
  const tab: Tab = tabs.includes(params.get('tab') as Tab) ? (params.get('tab') as Tab) : 'requests';

  return (
    <div className="dealer-page">
      <div className="dealer-page__header">
        <div>
          <h2>{t('dealer.credit.title')}</h2>
          <p className="dealer-muted">{t('dealer.credit.subtitle')}</p>
        </div>
        <div className="dealer-actions">
          <Link className="btn btn-primary" to="/dealer/credit/request">{t('dealer.credit.requestCta')}</Link>
          {walletEnabled && (
            <>
              <Link className="btn btn-secondary" to="/dealer/credit/packs">{t('dealer.credit.buyPackCta')}</Link>
              <Link className="btn btn-secondary" to="/dealer/credit/assign">{t('dealer.credit.assignCta')}</Link>
            </>
          )}
        </div>
      </div>
      <div className="pkg-tabs" role="tablist" aria-label={t('dealer.credit.title')}>
        {tabs.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`pkg-tab${tab === key ? ' pkg-tab--active' : ''}`}
            onClick={() => setParams({ tab: key }, { replace: true })}
          >
            {t(`dealer.credit.tab.${key}`)}
          </button>
        ))}
      </div>
      {tab === 'requests' && <RequestsTab />}
      {tab === 'balance' && walletEnabled && <BalanceTab />}
      {tab === 'assigned' && <AssignedTab />}
    </div>
  );
}

function RequestsTab() {
  const { t, lang } = useLanguage();
  const { token, canCommitSpend, walletEnabled } = useDealer();
  const [rows, setRows] = useState<CreditRequest[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState<CreditRequest | null>(null);

  const load = useCallback(async (next: string | null) => {
    if (!token) return;
    setLoading(true);
    try {
      const page = await dealerApi.listRequests(token, { cursor: next });
      setRows((prev) => (next ? [...prev, ...page.content] : page.content));
      setCursor(page.nextCursor);
      setHasMore(page.hasMore);
      setError(null);
    } catch (err) {
      setError(dealerErrorMessage(err, t));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => { load(null); }, [load]);

  const openProforma = async (r: CreditRequest) => {
    if (!token) return;
    try {
      const detail = await dealerApi.getRequest(r.id, token);
      if (detail.proformaUrl) window.open(detail.proformaUrl, '_blank', 'noopener');
      else setError(t('dealer.documents.notReady'));
    } catch (err) {
      setError(dealerErrorMessage(err, t));
    }
  };

  return (
    <>
      {!canCommitSpend && <CommitSpendNote />}
      {error && <div className="error-message">{error}</div>}
      {!loading && rows.length === 0 && !error && (
        <div className="dealer-empty">
          <p>{t('dealer.requests.empty')}</p>
          <div className="dealer-actions">
            <Link className="btn btn-primary" to="/dealer/credit/request">{t('dealer.credit.requestCta')}</Link>
            {walletEnabled && (
              <Link className="btn btn-secondary" to="/dealer/credit/packs">{t('dealer.credit.buyPackCta')}</Link>
            )}
          </div>
        </div>
      )}
      {rows.length > 0 && (
        <div className="dealer-table-wrap">
          <table className="dealer-table">
            <thead>
              <tr>
                <th>{t('dealer.requests.number')}</th>
                <th>{t('dealer.requests.kind')}</th>
                <th>{t('dealer.quote.faceValue')}</th>
                <th>{t('dealer.quote.totalDue')}</th>
                <th>{t('dealer.credits.status')}</th>
                <th>{t('dealer.requests.created')}</th>
                <th aria-label={t('dealer.common.actions')}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td dir="ltr">{r.number}</td>
                  <td>{t(r.kind === 'PACK' ? 'dealer.requests.kindPack' : 'dealer.requests.kindBuyer')}</td>
                  <td><Money value={r.faceValueAed} /></td>
                  <td><Money value={r.totalDueAed} /></td>
                  <td><StatusChip status={r.status} /></td>
                  <td>{formatDate(r.createdAt, lang)}</td>
                  <td className="dealer-row-actions">
                    {r.status === 'PROFORMA_ISSUED' && (r.kind !== 'PACK' || walletEnabled) && (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={!canCommitSpend}
                        title={canCommitSpend ? undefined : t('dealer.commit.disabledReason')}
                        onClick={() => setAccepting(r)}
                      >
                        {t('dealer.proforma.accept')}
                      </button>
                    )}
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => openProforma(r)}>
                      {t('dealer.requests.proforma')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {hasMore && (
        <button type="button" className="btn btn-secondary" disabled={loading} onClick={() => load(cursor)}>
          {t('dealer.common.loadMore')}
        </button>
      )}
      {accepting && token && (
        <ProformaAcceptDialog
          token={token}
          request={accepting}
          onClose={() => setAccepting(null)}
          onAccepted={(updated) => {
            setAccepting(null);
            setRows((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
          }}
        />
      )}
    </>
  );
}

function BalanceTab() {
  const { t, lang } = useLanguage();
  const { token } = useDealer();
  const [balance, setBalance] = useState<Balance | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    dealerApi.balance(token).then(setBalance).catch((err) => setError(dealerErrorMessage(err, t)));
  }, [token, t]);

  if (error) return <div className="error-message">{error}</div>;
  if (!balance) return <p className="dealer-muted">{t('dealer.common.loading')}</p>;
  return (
    <>
      <div className="dealer-stat dealer-stat--wide">
        <span className="dealer-stat__label">{t('dealer.balance.available')}</span>
        <Money className="dealer-stat__value" value={balance.availableAed} />
        <span className="dealer-muted">{t('dealer.balance.hint')}</span>
      </div>
      {balance.lots.length === 0 ? (
        <p className="dealer-muted dealer-empty-row">{t('dealer.balance.noLots')}</p>
      ) : (
        <div className="dealer-table-wrap">
          <table className="dealer-table">
            <thead>
              <tr>
                <th>{t('dealer.balance.source')}</th>
                <th>{t('dealer.quote.faceValue')}</th>
                <th>{t('dealer.credits.remaining')}</th>
                <th>{t('dealer.credits.status')}</th>
                <th>{t('dealer.credits.expires')}</th>
              </tr>
            </thead>
            <tbody>
              {balance.lots.map((l) => (
                <tr key={l.id}>
                  <td>{t(`dealer.balance.source.${l.source}`)}</td>
                  <td><Money value={l.faceValueAed} /></td>
                  <td><Money value={l.remainingAed} /></td>
                  <td><StatusChip status={l.status} /></td>
                  <td>{formatDate(l.expiresAt, lang)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function AssignedTab() {
  const { t } = useLanguage();
  const { token } = useDealer();
  const [status, setStatus] = useState('');
  const [rows, setRows] = useState<Credit[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (next: string | null) => {
    if (!token) return;
    setLoading(true);
    try {
      const page = await dealerApi.listCredits(token, { status: status || undefined, cursor: next });
      setRows((prev) => (next ? [...prev, ...page.content] : page.content));
      setCursor(page.nextCursor);
      setHasMore(page.hasMore);
      setError(null);
    } catch (err) {
      setError(dealerErrorMessage(err, t));
    } finally {
      setLoading(false);
    }
  }, [token, status, t]);

  useEffect(() => { load(null); }, [load]);

  return (
    <>
      <div className="dealer-toolbar">
        <label htmlFor="credit-status">{t('dealer.credits.status')}</label>
        <select id="credit-status" className="dealer-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">{t('dealer.common.all')}</option>
          {['PENDING', 'LIVE', 'EXPIRED', 'REVOKED'].map((s) => (
            <option key={s} value={s}>{t(`dealer.status.${s}`)}</option>
          ))}
        </select>
      </div>
      {error && <div className="error-message">{error}</div>}
      {!loading || rows.length > 0 ? <CreditSummaryTable credits={rows} /> : <p className="dealer-muted">{t('dealer.common.loading')}</p>}
      {hasMore && (
        <button type="button" className="btn btn-secondary" disabled={loading} onClick={() => load(cursor)}>
          {t('dealer.common.loadMore')}
        </button>
      )}
    </>
  );
}
